import { computed, toRef, watch, type MaybeRef } from 'vue';
import {
  ORDER_FEATURE_KEYS,
  type OrderFeatureKey,
} from '@cutr/constants/order-features';
import { useFlag } from '@/composables/useFlag';
import { useOrderFeaturesStore } from '@/stores/orderFeatures';
import { useOrderStore } from '@/stores/order';

export function useOrderFeature(
  orderId: MaybeRef<string | null | undefined>,
  featureKey: OrderFeatureKey,
  { onBackfillComplete }: { onBackfillComplete?: () => void } = {}
) {
  const orderIdRef = toRef(orderId);
  const { unleashFlagName, hasBackfillHook } = ORDER_FEATURE_KEYS[featureKey];
  const globalFlagEnabled = useFlag(unleashFlagName);
  const featuresStore = useOrderFeaturesStore();

  if (import.meta.env.DEV && !import.meta.env.VITEST) {
    const orderStore = useOrderStore();
    watch(
      [orderIdRef, () => orderStore.currentOrder.id],
      ([requested, active]) => {
        if (!requested || !active || requested === active) return;
        console.warn(
          `useOrderFeature asked for order ${requested} while ${active} is open; the features cache only holds the open order.`
        );
      },
      { immediate: true }
    );
  }

  // Callers pass a null order id to mean "not applicable here" — most often a
  // role gate — and rely on the feature reading as off.
  const featureState = computed(() =>
    orderIdRef.value ? featuresStore.getFeature(featureKey) : undefined
  );

  const backfillPending = computed(
    () =>
      hasBackfillHook &&
      (featureState.value?.enabled ?? false) &&
      !featureState.value?.backfillCompletedAt
  );

  if (onBackfillComplete) {
    watch(backfillPending, (isPending, wasPending) => {
      if (wasPending && !isPending) {
        onBackfillComplete();
      }
    });
  }

  const featureEnabled = computed(
    () => globalFlagEnabled.value && (featureState.value?.enabled ?? false)
  );
  const backfillCompletedAt = computed(
    () => featureState.value?.backfillCompletedAt ?? null
  );

  return {
    globalFlagEnabled,
    featureEnabled,
    backfillPending,
    backfillCompletedAt,
    isLoading: computed(() => featuresStore.isLoading),
    toggle: (payload: { enabled: boolean; clearExisting?: boolean }) =>
      featuresStore.toggleFeature({
        orderId: orderIdRef.value!,
        featureKey,
        payload,
      }),
    isToggling: computed(() => featuresStore.isToggling(featureKey)),
  };
}
